import theme from "assets/theme";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { StyleSheet, Text, View } from "react-native";
import { validateEmail } from "shared/utils/validator.utils";
import { ROLE_NAMES, ROLE_USER } from "web/constants";
import TooltipText from "webComponentsFormElements/TooltipText.component";
import Dropdown from "webComponentsFormElements/inputs/Dropdown.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import { OrganizationContext } from "webContext/Organization.context";
import UserImageUpload from "./UserImageUpload.component";

const classes = StyleSheet.create({
  root: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 20,
  },
  row: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 20,
    width: "100%",
  },
  input: {
    flex: 1,
    maxHeight: "80%",
    maxWidth: "50%",
  },
  dropDownContainer: {
    flexDirection: "column",
    justifyContent: "center",
    marginTop: 10,
    marginBottom: 10,
  },
  requiredText: {
    color: theme.color.error,
  },
  dropDownFontStyle: {
    fontSize: 16,
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
  },
  infoNote: {
    fontSize: 14,
    color: theme.color.darkGrey,
    textAlign: "center",
    marginTop: 30,
    fontStyle: "italic",
  },
});

export default function CreatePersonForm(props) {
  const {
    setCanSave,
    setUserToUpsert,
    selectedImageFile,
    setSelectedImageFile,
    setPreviewImageUrl,
  } = props;

  const { organizationColor } = useContext(OrganizationContext);
  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        dropDown: {
          backgroundColor: theme.color.background,
          borderRadius: 8,
          borderWidth: 2,
          padding: 10,
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
          borderColor: organizationColor,
        },
      }),
    [organizationColor],
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(ROLE_USER);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);

  // Handle image changes from UserImageUpload
  const handleImageChange = useCallback((imageUrl, imageBlob) => {
    setPreviewImageUrl(imageUrl);
    setSelectedImageFile(imageBlob);
    // Validation will happen in useEffect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Prop functions should be stable

  // Simple form validation for create user
  const validateForm = useCallback((fName, lName, emailVal, role, imgFile) => {
    const isValid =
      fName?.length > 0 &&
      lName?.length > 0 &&
      emailVal?.length > 0 &&
      validateEmail(emailVal) &&
      role;

    setCanSave(isValid);

    if (isValid) {
      setUserToUpsert({
        firstName: fName,
        lastName: lName,
        email: emailVal,
        roleId: role,
        ...(imgFile && { selectedImageFile: imgFile }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies needed for create form

  // Validate form whenever any field changes
  useEffect(() => {
    validateForm(firstName, lastName, email, roleId, selectedImageFile);
  }, [firstName, lastName, email, roleId, selectedImageFile, validateForm]);
  const getZIndex = (value) => ({
    zIndex: value,
  });

  return (
    <View style={classes.root}>
      <UserImageUpload onImageChange={handleImageChange} />

      <View style={classes.row}>
        <View style={classes.input}>
          <LabelInput
            label="First Name"
            value={firstName}
            onChangeText={setFirstName}
            required
          />
        </View>
        <View style={classes.input}>
          <LabelInput
            label="Last Name"
            value={lastName}
            onChangeText={setLastName}
            required
          />
        </View>
      </View>
      <View style={classes.row}>
        <View style={classes.input}>
          <LabelInput
            label="Email"
            value={email}
            onChangeText={setEmail}
            required
          />
        </View>
        <View style={classes.input}>
          <View style={classes.dropDownContainer}>
            <View style={getZIndex(1)}>
              <TooltipText
                tooltip={
                  "Admins can manage the organization, organization admins can also delete the organization."
                }
              >
                Role <Text style={classes.requiredText}>*</Text>{" "}
              </TooltipText>
            </View>
            <View style={[getZIndex(0), stateClasses.dropDown]}>
              <Dropdown
                options={Object.entries(ROLE_NAMES).map(
                  ([entryRoleId, entryRoleName]) => ({
                    label: entryRoleName,
                    value: entryRoleId,
                  }),
                )}
                selected={roleId}
                onSelect={setRoleId}
                textStyle={classes.dropDownFontStyle}
                onOpenChange={setIsRoleDropdownOpen}
              />
            </View>
          </View>
        </View>
      </View>

      <Text style={[classes.infoNote, isRoleDropdownOpen && { marginTop: 130 }]}>
        Upon saving, you will be redirected to the Manage Person Page
      </Text>
    </View>
  );
}
